/**
 * Sélecteur de SKILLCARD en modale (remplace la liste déroulante) : recherche
 * (titre sans LaTeX, domaine, sans accents — `matchesCardFilters`), filtre par
 * niveau en puces, ligne avec la créature origami dans la teinte de la carte,
 * étoiles de rareté, domaine et niveaux. Choix facultatif « aucune » possible
 * (carte mère d'une nouvelle carte).
 */

import { useState } from 'react';
import { Check, CircleOff } from 'lucide-react';
import { MathText } from '@jeraisonne/ui';
import { OrigamiCreature, formatCardLevels } from '@jeraisonne/ui/tool-cards';
import {
  CARD_RARITY_LABELS,
  CARD_RARITY_STARS,
  cardLevelOptions,
  cardPalette,
  filterCards,
  stripMath,
  type CardColorTheme,
  type CardRarity,
} from '@jeraisonne/db/cards';
import {
  PickerField,
  PickerModal,
  PickerOption,
  type PickerChip,
} from '@/components/pickers/picker-modal';

export interface SkillcardPickerItem {
  id: string;
  title: string;
  levels: readonly string[];
  rarity: CardRarity;
  colorTheme: CardColorTheme;
  creature: unknown;
  domainLabel: string | null;
}

/** Ligne `cards` (copie de travail) → élément du sélecteur. */
export function toSkillcardPickerItem(c: {
  id: string;
  title: string;
  levels?: readonly string[] | null;
  rarity: CardRarity;
  color_theme: CardColorTheme;
  creature: unknown;
  domain_label: string | null;
}): SkillcardPickerItem {
  return {
    id: c.id,
    title: c.title,
    levels: c.levels ?? [],
    rarity: c.rarity,
    colorTheme: c.color_theme,
    creature: c.creature,
    domainLabel: c.domain_label,
  };
}

/** Filtre pur (recherche + niveau) — même règle que les listes de SKILLCARDS. */
export function filterSkillcardPickerItems(
  items: readonly SkillcardPickerItem[],
  query: string,
  level: string,
): SkillcardPickerItem[] {
  return filterCards(
    items,
    (c) => ({ title: c.title, levels: c.levels, domainLabel: c.domainLabel }),
    { q: query, level },
  );
}

export function skillcardLevelChips(items: readonly SkillcardPickerItem[]): PickerChip[] {
  return cardLevelOptions(items.map((c) => ({ levels: c.levels })))
    .filter((l) => l.count > 0)
    .map((l) => ({ value: l.value, label: l.label, count: l.count }));
}

function CardLine({ item }: { item: SkillcardPickerItem }) {
  const palette = cardPalette(item.levels, item.colorTheme);
  const sub = [item.domainLabel, item.levels.length > 0 ? formatCardLevels(item.levels) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      <span
        aria-hidden
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border-2"
        style={{ background: palette.bg, borderColor: palette.accent }}
      >
        <OrigamiCreature creature={item.creature} accent={palette.accent} className="h-8 w-8" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 line-clamp-2 block text-sm font-semibold">
          <MathText>{item.title}</MathText>
        </span>
        <span className="text-ink-500 flex items-center gap-1.5 truncate text-xs">
          <span
            className="text-accent-500 tracking-tight"
            aria-label={CARD_RARITY_LABELS[item.rarity]}
          >
            {'★'.repeat(CARD_RARITY_STARS[item.rarity])}
          </span>
          {sub && <span className="truncate">{sub}</span>}
        </span>
      </span>
    </>
  );
}

export function SkillcardPickerField({
  items,
  value,
  onChange,
  label,
  placeholder = 'Choisir une SKILLCARD…',
  title = 'Choisir une SKILLCARD',
  noneLabel,
  fallbackLabel,
  disabled,
  sections,
}: {
  items: readonly SkillcardPickerItem[];
  /**
   * Regroupe la liste sous des intertitres (ex. packs en cours / passés / à
   * venir). Chaque carte doit figurer dans `items` ET dans une seule section.
   */
  sections?: readonly { key: string; label: string; items: readonly SkillcardPickerItem[] }[];
  value: string | null;
  /** `null` = « aucune » (seulement si `noneLabel` est fourni). */
  onChange: (id: string | null) => void;
  label?: string;
  placeholder?: string;
  title?: string;
  /** Propose un choix « aucune carte » (ex. « Aucune — carte de départ »). */
  noneLabel?: string;
  /** Libellé du choix courant s'il n'est pas dans `items` (ex. mère archivée). */
  fallbackLabel?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState('');
  const selected = items.find((c) => c.id === value) ?? null;
  const shown = filterSkillcardPickerItems(items, query, level);
  const pick = (id: string | null) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <>
      <PickerField
        label={label}
        placeholder={noneLabel && !value ? noneLabel : placeholder}
        disabled={disabled}
        onOpen={() => setOpen(true)}
        selected={
          selected ? (
            <span className="flex min-w-0 items-center gap-3">
              <CardLine item={selected} />
            </span>
          ) : value && fallbackLabel ? (
            <span className="text-ink-900 font-semibold">
              <MathText>{fallbackLabel}</MathText>
            </span>
          ) : null
        }
      />
      {open && (
        <PickerModal
          title={title}
          query={query}
          onQuery={setQuery}
          searchPlaceholder="Rechercher une SKILLCARD (titre, domaine)…"
          chips={skillcardLevelChips(items)}
          chipValue={level}
          onChip={setLevel}
          allChipsLabel="Tous les niveaux"
          onClose={() => setOpen(false)}
        >
          <div role="listbox" aria-label={title} className="space-y-1">
            {noneLabel && !query && !level && (
              <PickerOption label={noneLabel} selected={!value} onSelect={() => pick(null)}>
                <span className="bg-ink-100 text-ink-500 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg">
                  <CircleOff className="h-5 w-5" aria-hidden />
                </span>
                <span className="text-ink-700 flex-1 text-sm font-semibold">{noneLabel}</span>
                {!value && <Check className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />}
              </PickerOption>
            )}
            {sections
              ? sections.map((sec) => {
                  const secShown = filterSkillcardPickerItems(sec.items, query, level);
                  if (secShown.length === 0) return null;
                  return (
                    <div key={sec.key} role="group" aria-label={sec.label} className="space-y-1">
                      <p className="text-ink-500 px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide">
                        <MathText>{sec.label}</MathText>
                      </p>
                      {secShown.map((c) => (
                        <PickerOption
                          key={c.id}
                          label={stripMath(c.title)}
                          selected={c.id === value}
                          onSelect={() => pick(c.id)}
                        >
                          <CardLine item={c} />
                          {c.id === value && (
                            <Check className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />
                          )}
                        </PickerOption>
                      ))}
                    </div>
                  );
                })
              : shown.map((c) => (
                  <PickerOption
                    key={c.id}
                    label={stripMath(c.title)}
                    selected={c.id === value}
                    onSelect={() => pick(c.id)}
                  >
                    <CardLine item={c} />
                    {c.id === value && (
                      <Check className="text-primary-600 h-5 w-5 shrink-0" aria-hidden />
                    )}
                  </PickerOption>
                ))}
            {shown.length === 0 && (
              <p className="text-ink-500 px-3 py-8 text-center text-sm">
                {items.length === 0
                  ? 'Aucune SKILLCARD disponible.'
                  : 'Aucune SKILLCARD ne correspond.'}
              </p>
            )}
          </div>
        </PickerModal>
      )}
    </>
  );
}
